import { getInsightDetailsByAudience } from "@/api/backend/insights/detail";
import { EnrichedCartItemType } from "@/models/cart";
import { resolveInsightDetails } from "@/pages/Order/Cart/utils";
import type { InsightReportType } from "@/types/insight";
import { AudienceSchemaType } from "@/pages/Store/CreateInsight/utils";
import { dateToLocaleMDDYYYY } from "@/utils/functions/date";
import { exportInsightXLSX } from "@/utils/functions/excel";
import PQueue from "p-queue";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { OrderType } from "@/models/order";

// accepts and order and an item
export function useExportInsightData() {
  const exportQueue = useRef<PQueue>(new PQueue({ concurrency: 1 }));

  const exportControllers = useRef<Record<string, AbortController>>({});
  const [exportLoading, setExportLoading] = useState<string[]>([]);

  const getAudienceDetails = async ({
    orderId,
    itemId,
    audienceId,
  }: {
    orderId: string;
    itemId: number;
    audienceId: string;
  }): Promise<InsightReportType | undefined> => {
    try {
      const controllerKey = `${orderId}-${itemId}-${audienceId}`;

      // Abort any ongoing request for this key
      if (exportControllers.current[controllerKey]) {
        exportControllers.current[controllerKey].abort();
      }
      // Create new controller and store it
      const controller = new AbortController();
      exportControllers.current[controllerKey] = controller;

      // Fetch audience details
      const response = await getInsightDetailsByAudience({
        orderId: orderId,
        itemId: itemId,
        audienceId: audienceId,
        aController: controller,
      });
      if (
        "status" in response &&
        (response.status === "error" || response.status === "aborted")
      ) {
        throw new Error(
          response.error?.error.message || "Failed to fetch audience details."
        );
      }

      return response as InsightReportType;
    } catch (err) {
      console.log("Error fetching audience insight details:", err);
    }
  };

  const exportInsightData = async ({
    order,
    item,
  }: {
    order: OrderType;
    item: EnrichedCartItemType;
  }) => {
    try {
      if (!order || !order.order_id)
        throw new Error("Order is not available for export.");
      if (!item.id || !item.item_data || item.item_data.length === 0)
        throw new Error("Item data is not available for export.");

      setExportLoading((prev) => [...prev, `${order.order_id}-${item.id}`]);

      // An Insight item contains an id (purchase id for that item), and an item_data array with contains the details for the audience(s)
      const insightDetails = resolveInsightDetails(item);

      if (!insightDetails)
        throw new Error("Insight details could not be resolved.");

      const { country, timeframe, audiences, question } = insightDetails;
      if (!country || !timeframe || !audiences || !question)
        throw new Error("Invalid insight details for export.");

      // 1. Make an export for each audience's data. One tab each.

      const queueLargeExport = async (audiences: AudienceSchemaType[]) => {
        // Clear any existing queue
        exportQueue.current.clear();

        // let { current: abortControllers } = exportAbortControllers;

        // Create the List of Requests Required

        const data: InsightReportType[] = [];

        async function fetchPageData(audienceId: string) {
          const response = await getAudienceDetails({
            orderId: order.order_id as string,
            itemId: item.id,
            audienceId: audienceId,
          });

          if (!response) {
            throw new Error("Failed to fetch audience details for export.");
          }

          data.push(response);
        }

        const fetchPromises = audiences.map((audience) => {
          return exportQueue.current.add(() =>
            fetchPageData(audience.audience_id)
          );
        });

        // Wait for all fetches to complete
        await Promise.all(fetchPromises);

        return { data: data };
      };

      const response = await queueLargeExport(audiences);

      if (!response || !response.data || response.data.length === 0)
        throw new Error("No data available for export.");
      // 2. Export the data to Excel
      exportInsightXLSX({
        reportData: response.data,
        exportType: "standard-multiple",
        name: `Insight - ${response.data[0].name} - ${dateToLocaleMDDYYYY(
          new Date()
        )}`,
      });
    } catch (err) {
      console.log(err);
      toast.error("Failed to export insight data.");
    } finally {
      setExportLoading((prev) =>
        prev.filter((key) => key !== `${order.order_id}-${item.id}`)
      );
    }
  };

  return { exportLoading, exportInsightData };
}
